/**********************************************************************
 * Drawer form that holds the details for a cart item 🐺
 * *****************************************************
 *
 * @format */

import {prettyProductLabel, prettyProductShape} from 'frontier/ds/quoter/functions/format';
import {Button, Icon, Modal, ModalHeader, ModalFooter} from '@sttark/design-system';
import ImageViewer from '../../../../admin/components/image_viewer';
import OrderMeta, {prettyProductMeta} from '../../cart/order_meta';
import ModalPortal from 'frontier-admin/modal/container_portal';
import {SingleDesignQty} from '../field/single_design_qty';
import React, {useState} from 'react';

import {
    ORDER_EXPEDITED_PROCESSING,
    ORDER_STANDARD_PROCESSING,
    PRODUCT_TYPE_CARTON,
    PRODUCT_TYPE_GRAPHIC_DESIGN,
    PRODUCT_TYPE_LABEL,
    SHAPE_TYPES
} from 'frontier-constants';

// styles
import './cart_form.sass';
import {ItemDetails} from 'frontier/cart/components/cart_overview/form/item_details';
import {IfElse} from '../../../../utilities';
import {singlePrettyAddress} from '../../../helpers/format';

export const CartForm = ({cart, cta}) => {
    const [showModal, setShowModal] = useState(0);

    // check whether this comp is being called by graphic design or cartons/ labels
    const isGraphicDesign = cart.product_type_id === PRODUCT_TYPE_GRAPHIC_DESIGN;
    const isCarton = cart.product_type_id === PRODUCT_TYPE_CARTON;
    const isLabel = cart.product_type_id === PRODUCT_TYPE_LABEL;

    // extrapolate current processing time and assign proper label
    // if no label matching value can be found, fallback to 'Default'
    let valueLabel = 'Default';

    const delayDaysLabel = [cart.delay_total, cart.delay_total === 1 ? 'Day' : 'Days'].join(' ');
    const options = [
        {
            id: ORDER_STANDARD_PROCESSING,
            label: `Approximately - ${delayDaysLabel}`
        },
        {
            id: ORDER_EXPEDITED_PROCESSING,
            label: `Guaranteed - ${delayDaysLabel}`
        }
    ];

    function getOptionLabel(option) {
        if (option.id == cart.processing_id) valueLabel = option.label;
    }

    if (cart.processing_id) options.forEach(getOptionLabel);

    // printed proof type data differs for deisgns and labels / cartons
    let proofType;
    if (cart.product_type_id === PRODUCT_TYPE_GRAPHIC_DESIGN)
        if (cart.proof_type_label === 'object')
            // some items were returning an obejct rather than a string
            // this accounts for it.
            proofType = cart.proof_type_id ? cart.proof_type_label[cart.proof_type_id] : '';
        else proofType = cart.is_printed_proof_only ? 'Printed Proof Only' : cart.proof_type_label;
    else proofType = cart.is_printed_proof_only ? 'Printed Proof Only' : cart.proof_type_label;

    // get the product meta properties
    const additionalOptions = prettyProductMeta(cart);

    // get the type of product to display correct title
    const productType =
        cart.product_type_id === 1 ? 'labels' : cart.product_type_id === 1 ? 'cartons' : 'design';

    let shape, color;

    shape = prettyProductShape(cart.shape_label, cart?.is_square_corners, cart.shape_id);

    return (
        <>
            {/* Edit Modal */}
            <Modal
                className="view-invoice-75cs"
                onClose={() => setShowModal(0)}
                isOpen={showModal > 0}
                zIndex={3000}
                bodyHeightAuto>
                <IfElse condition={showModal === 1}>
                    <ModalHeader onClose={() => setShowModal(0)}>Edit Item</ModalHeader>
                    <ModalHeader onClose={() => setShowModal(0)}>Upload Work</ModalHeader>
                </IfElse>
                <div>
                    <h5 className="header_subtitle font-heading-md">
                        You will be taken back to the Quoter.
                    </h5>
                    <IfElse condition={showModal === 1}>
                        <div className="card_content-holder">
                            From there, you can edit all details of this item. Changes to this cart
                            item will be automatically saved and the item will be updated in the
                            cart.{' '}
                        </div>
                        <div className="card_content-holder">
                            From there, you can upload artwork. Changes to this cart item will be
                            automatically saved.{' '}
                        </div>
                    </IfElse>
                </div>
                <ModalFooter>
                    <div className="d-flex align-items-center justify-content-end gap-4">
                        <button onClick={() => setShowModal(0)} className="btn btn--secondary">
                            <b>Cancel</b>
                        </button>
                        <button
                            onClick={() =>
                                (window.location = `/orders/custom-${productType}/${cart.id}?showErrorsFromCart=true&edit`)
                            }
                            className="btn btn--primary">
                            <b>Edit Item</b>
                        </button>
                    </div>
                </ModalFooter>
            </Modal>
            <ModalPortal noCard>
                <div className="cart-form bg-structure-white">
                    {/*header*/}
                    <div className="cart-form_header">
                        <h5 className="header_heading m-0">
                            {!isGraphicDesign
                                ? prettyProductLabel(cart.size_description, cart.product_type_id)
                                : prettyProductLabel(
                                      cart.graphic_design_order_details.product_label,
                                      cart.product_type_id
                                  )}
                        </h5>
                        <div className="header_buttons">
                            <div>
                                {!cart.is_reorder && (
                                    <Button
                                        label="Edit item"
                                        onClick={() => setShowModal(1)}
                                        primary
                                        large
                                    />
                                )}
                            </div>
                            <div>
                                <Button label="Close" onClick={cta.handleClose} secondary large />
                            </div>
                        </div>
                    </div>

                    {/* item details */}
                    <section className="cart-form_section cart-form_item-details container-sm">
                        <h5 className="section_heading">Item Details</h5>
                        <div className="row meta-container">
                            <ItemDetails
                                isFlatPrintedProof={cart.is_flat_printed_proof}
                                has_variable_data={cart.has_variable_data}
                                form_factor_label={cart.form_factor_label}
                                substrate_label={cart.substrate_label}
                                laminate_label={cart.laminate_label}
                                backing_label={cart.backing_label}
                                pantone_qty={cart.pantone_qty}
                                design_qty={cart.design_qty}
                                total_qty={cart.total_qty}
                                graphics={isGraphicDesign}
                                valueLabel={valueLabel}
                                proofType={proofType}
                                cartons={isCarton}
                                labels={isLabel}
                                shape={shape}
                                {...cart}
                            />

                            {/*removing color here per crystal/jonah rec*/}
                            {/*<Color {...cart} />*/}
                        </div>
                    </section>

                    {/* Additional Options */}
                    {additionalOptions.length > 0 && (
                        <section className="cart-form_section">
                            <h5 className="section_heading">Additional Options</h5>
                            <OrderMeta
                                isGraphicDesignOrder={isGraphicDesign}
                                values={additionalOptions}
                            />
                        </section>
                    )}

                    {/* Designs */}
                    {cart.product_type_id !== PRODUCT_TYPE_GRAPHIC_DESIGN && !cart.is_blank && (
                        <section className="cart-form_section uploaded-files">
                            <h5 className="section_heading">
                                {isGraphicDesign ? 'Design Files' : 'Uploaded Files'}
                            </h5>

                            {/* Design table*/}
                            {cart.designs_orders.length > 0 ? (
                                <div className="p-0 section_uploaded-files d-flex align-items-center justify-content-center flex-wrap">
                                    {/* header */}
                                    <h6 className="uploaded-files_title title-df m-0 bg-structure-lightest">
                                        {isGraphicDesign ? 'Files' : 'Uploaded Design Files'}
                                    </h6>
                                    <h6 className="uploaded-files_title title-qty  m-0 bg-structure-lightest">
                                        Quantity
                                    </h6>

                                    {/* table*/}
                                    {cart.designs_orders.map((design, i) => {
                                        return (
                                            <div
                                                key={i}
                                                className="cart-order-design uploaded-files_row d-flex align-items-center justify-content-center">
                                                {/* design url  */}
                                                <div className="row_description row_file-name d-flex align-items-center justify-content-center">
                                                    <div className="description_image-viewer">
                                                        <ImageViewer
                                                            shouldUseAdaptiveImage={false}
                                                            filename={design.filename}
                                                            style={{zIndex: 3001}}
                                                            src={design.signed_url}
                                                            maxWidth="800px">
                                                            <div>
                                                                <button
                                                                    className="btn btn-link tw-p-0 text-truncate"
                                                                    title={
                                                                        design.filename_without_id
                                                                    }>
                                                                    <span>
                                                                        <Icon
                                                                            name="view"
                                                                            color="#000000"
                                                                            small
                                                                        />
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        </ImageViewer>
                                                    </div>
                                                    <p>{design.filename_without_id}</p>
                                                </div>

                                                {/* Design Qty */}
                                                {/*<p className="row_description">{design.qty}</p>*/}
                                                <div className="row_description">
                                                    <SingleDesignQty
                                                        {...cart}
                                                        index={i}
                                                        designOrder={design}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="designs_missing_design d-flex align-items-center justify-content-start">
                                    <p className="m-0">
                                        <span>
                                            <Icon name="warning" color="#e5b200" small />
                                        </span>
                                        You still need to
                                    </p>
                                    <h5
                                        className="color-action-primary m-0"
                                        onClick={() => setShowModal(2)}>
                                        upload artwork.
                                    </h5>
                                </div>
                            )}
                        </section>
                    )}

                    {/* Order Notes */}
                    <section className="cart-form_section cart-form--order-notes">
                        <h5 className="section_heading section_order-notes">Order Notes</h5>

                        {/* additional comments */}
                        <div className="cart-field cart-form_field">
                            <label className="cart-field-label ">Additional Comments</label>
                            {!isGraphicDesign && (
                                <p className="cart-field-prop">
                                    {cart.comments_customer
                                        ? cart.comments_customer
                                        : 'No comments'}
                                </p>
                            )}

                            {isGraphicDesign && (
                                <p className="cart-field-prop">
                                    {cart.graphic_design_order_details.comments_graphic_design
                                        ? cart.graphic_design_order_details.comments_graphic_design
                                        : 'No coments'}
                                </p>
                            )}
                        </div>

                        {/* design instructions */}
                        {!isGraphicDesign && (
                            <div className="cart-field cart-form_field">
                                <label className="cart-field-label">Artwork Instructions</label>
                                <p className="cart-field-prop">
                                    {cart.comments_preprinting
                                        ? cart.comments_preprinting
                                        : 'No comments'}
                                </p>
                            </div>
                        )}
                    </section>
                </div>
            </ModalPortal>
        </>
    );
};
